import { doc, getDocs, limit, writeBatch, deleteDoc } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { DELETE_BATCH_SIZE } from "../constants";
import { createBackupsBatched } from "./backupService";
import { commitBatches } from "./firestoreDataService";
import { registerCollection, touchCollection } from "../../../services/collectionRegistryService";
import {
  buildConditionQuery,
  countMatchingDocuments,
  fetchMatchingPage,
  parseFilterValue,
} from "./conditionQueryService";
import { ensureFreshAuthToken } from "./operationAuthService";

const DELETE_SUB_BATCH = 100;

export { parseFilterValue, buildConditionQuery };

export async function dryRunDeleteByCondition(collectionName, filter) {
  const col = collectionName.trim();
  if (!col) throw new Error("Collection is required");
  if (!String(filter.field || "").trim()) throw new Error("Field is required");

  const matchCount = await countMatchingDocuments(col, filter);

  const previewSnap = await getDocs(buildConditionQuery(col, filter, [limit(20)]));
  const previewIds = previewSnap.docs.map((d) => d.id);

  const batchCount = matchCount === 0 ? 0 : Math.ceil(matchCount / DELETE_BATCH_SIZE);

  return {
    affectedDocuments: matchCount,
    previewIds,
    estimatedBatches: batchCount,
    estimatedWrites: matchCount,
    estimatedSeconds: Math.max(1, Math.ceil(matchCount / DELETE_BATCH_SIZE) * 2),
    warnings: [
      "Documents will be permanently deleted unless restored from backup.",
      matchCount > 1000 ? `Large delete: ${matchCount.toLocaleString()} documents.` : null,
    ].filter(Boolean),
    filter: { field: filter.field, op: filter.op, value: filter.value },
  };
}

async function fetchAllMatchingDocuments(collectionName, filter) {
  const all = [];
  let cursor = null;
  while (true) {
    const page = await fetchMatchingPage(collectionName, filter, { pageSize: 500, cursor });
    page.rows.forEach((r) => all.push({ id: r.id, data: r.data }));
    if (!page.hasMore) break;
    cursor = page.cursor;
  }
  return all;
}

async function deleteDocumentsChunk(col, chunk) {
  const operations = chunk.map((m) => ({
    type: "delete",
    ref: doc(db, col, m.id),
  }));

  try {
    await commitBatches(operations);
    return { deleted: chunk.length, failed: 0, failedIds: [], errors: [] };
  } catch (batchErr) {
    console.warn("[deleteByCondition] batch delete failed, retrying smaller chunks", batchErr);
    let deleted = 0;
    let failed = 0;
    const failedIds = [];
    const errors = [batchErr?.message || String(batchErr)];

    for (let i = 0; i < chunk.length; i += DELETE_SUB_BATCH) {
      const sub = chunk.slice(i, i + DELETE_SUB_BATCH);
      try {
        const batch = writeBatch(db);
        sub.forEach((m) => batch.delete(doc(db, col, m.id)));
        await batch.commit();
        deleted += sub.length;
      } catch (subErr) {
        for (const m of sub) {
          try {
            await deleteDoc(doc(db, col, m.id));
            deleted += 1;
          } catch (docErr) {
            failed += 1;
            failedIds.push(m.id);
            errors.push(`${m.id}: ${docErr?.message || docErr}`);
          }
        }
      }
    }

    return { deleted, failed, failedIds, errors };
  }
}

/**
 * Backup all matches, then delete in WriteBatch chunks (max 500 per batch).
 * onProgress({ phase, batch, totalBatches, message })
 */
export async function executeDeleteByCondition({
  collectionName,
  filter,
  adminUid,
  adminName,
  onProgress,
}) {
  const col = collectionName.trim();
  const started = Date.now();
  await ensureFreshAuthToken();
  await registerCollection(col, { createdBy: adminUid || adminName || null });
  await touchCollection(col, adminUid || adminName || null);

  onProgress?.({ phase: "fetching", message: "Loading matching documents…" });
  const matches = await fetchAllMatchingDocuments(col, filter);

  if (!matches.length) {
    return { deleted: 0, failed: 0, seconds: 0, documentIds: [], backups: [], errors: [] };
  }

  onProgress?.({ phase: "backup", message: `Backing up ${matches.length} document(s)…` });
  const documents = {};
  matches.forEach((m) => {
    documents[m.id] = m.data;
  });

  const backupIds = await createBackupsBatched({
    collectionName: col,
    documents,
    adminUid,
    adminName,
    operation: "delete_by_condition",
    chunkSize: 100,
    onProgress: ({ done, total }) => {
      onProgress?.({
        phase: "backup",
        message: `Backing up ${done} / ${total}…`,
      });
    },
  });

  await ensureFreshAuthToken();

  const totalBatches = Math.ceil(matches.length / DELETE_BATCH_SIZE);
  let deleted = 0;
  let failed = 0;
  const deletedIds = [];
  const failedIds = [];
  const errors = [];

  for (let i = 0; i < matches.length; i += DELETE_BATCH_SIZE) {
    const batchNum = Math.floor(i / DELETE_BATCH_SIZE) + 1;
    const chunk = matches.slice(i, i + DELETE_BATCH_SIZE);

    onProgress?.({
      phase: "deleting",
      batch: batchNum,
      totalBatches,
      message: `Deleting… Batch ${batchNum} of ${totalBatches}`,
    });

    const outcome = await deleteDocumentsChunk(col, chunk);
    deleted += outcome.deleted;
    failed += outcome.failed;
    const chunkFailed = new Set(outcome.failedIds);
    chunk.forEach((m) => {
      if (chunkFailed.has(m.id)) failedIds.push(m.id);
      else deletedIds.push(m.id);
    });
    if (outcome.errors?.length) errors.push(...outcome.errors);
  }

  const seconds = Math.round((Date.now() - started) / 1000);

  return {
    deleted,
    failed,
    seconds,
    documentIds: deletedIds,
    failedIds,
    backups: backupIds,
    matchCount: matches.length,
    errors: [...new Set(errors)].slice(0, 5),
  };
}
